import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { SOURCE_ALKOHOLLAGEN, SOURCE_FOHM_VAGLEDNING, SOURCE_PROP_2025_26, LAST_REVIEWED } from "@/lib/official-sources";


const faqs = [
  {
    q: "Vad förändras 1 juni 2026?",
    a: "Riksdagen har beslutat att slopa kravet på eget kök och kravet på ett varierat utbud av lagad eller på annat sätt tillredd mat. Matservering är därmed inte längre ett villkor för att få serveringstillstånd. Verksamheter som vill servera mat får så klart fortsätta göra det – men det prövas inte längre som ett tillståndskrav.",
  },
  {
    q: "Måste restauranger fortfarande servera mat?",
    a: "Nej, inte som villkor för tillståndet. Kommunen får från 1 juni 2026 inte ställa krav på matutbud, köksinredning eller meny vid prövning av serveringstillstånd. Många restauranger kommer naturligtvis att fortsätta servera mat – men det är ett affärsbeslut, inte ett tillståndsvillkor.",
  },
  {
    q: "Försvinner kravet på lämplig lokal?",
    a: "Nej. Lokalen ska fortfarande vara lämplig ur brand-, ordnings- och säkerhetssynpunkt. Det är bara mat- och köksparagrafen som ändras.",
  },
  {
    q: "Vad gäller för barer och nattklubbar?",
    a: "Rena drinkbarer, vinbarer och nattklubbar som tidigare fått avslag på grund av att de saknade kök eller varierad meny kan efter 1 juni 2026 få tillstånd om övriga krav (lämplighet, lokal, kunskapsprov, ordning) uppfylls.",
  },
  {
    q: "Vad gäller för caféer?",
    a: "Caféer som tidigare nekats för att de bara serverade bakverk och smörgåsar kan nu kvalificera sig. Matutbudet är inte längre ett tillståndsvillkor.",
  },
  {
    q: "Måste jag göra om kunskapsprovet?",
    a: "Nej, befintliga PBI behöver inte göra om provet på grund av lagändringen. Vid nyetablering, ny PBI eller väsentliga ändringar gäller som tidigare att minst en PBI ska klara provet.",
  },
  {
    q: "Hur många frågor är kunskapsprovet på?",
    a: "Frågeantalet varierar beroende på tillståndstyp och kommun. Folkhälsomyndigheten anger områden och miniminivåer i FoHMFS 2014:7; kontrollera aktuell omfattning hos din kommun.",
  },
  {
    q: "Påverkas tillsynen?",
    a: "Tillsynen av ordning, nykterhet, ålderskontroll och marknadsföring fortsätter som vanligt. Det är kraven vid prövning av nya tillstånd som lättats – inte den löpande tillsynen.",
  },
];


export const Route = createFileRoute("/nya-regler-serveringstillstand-2026")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Nya regler 2026" },
      ]}
      title="Nya regler för serveringstillstånd 2026 – köks- och matkravet slopas"
      intro="Från och med 1 juni 2026 har riksdagen tagit bort kravet på eget kök och varierat matutbud som villkor för att få serveringstillstånd. Här är en sammanfattning av vad som faktiskt ändras, vad som fortfarande gäller och hur det påverkar restauranger, barer, caféer och cateringföretag."
      lastReviewed={LAST_REVIEWED}
      sources={[SOURCE_PROP_2025_26, SOURCE_ALKOHOLLAGEN, SOURCE_FOHM_VAGLEDNING]}
      sections={[
        {
          h2: "Det här ändras 1 juni 2026",
          body: "Riksdagen har beslutat att slopa de tidigare kraven i alkohollagen på att serveringsstället ska ha ett eget kök och tillhandahålla ett mångsidigt utbud av lagad eller på annat sätt tillredd mat. Efter 1 juni 2026 är matservering inte längre ett villkor för serveringstillstånd. Kraven har länge ansetts otidsenliga och hindrat etablering av t.ex. cocktailbarer, vinbarer och konceptserveringar utan eget kök.",
        },
        {
          h2: "Det här gäller fortfarande",
          body: "Övriga krav i alkohollagen är oförändrade: personlig och ekonomisk lämplighet, godkänt kunskapsprov för minst en PBI, lämplig lokal ur brand- och ordningssynpunkt, rutiner mot överservering och underårig konsumtion samt marknadsföringsreglerna i 7 kap. Det är bara köks- och matparagrafen som tas bort – allt annat består.",
        },
        {
          h2: "Vad betyder det i praktiken?",
          body: "En bar eller restaurang behöver inte längre visa kök, meny eller mat­leverantör vid tillståndsprövningen. Verksamheter som ändå vill servera mat får göra det fritt – via eget kök, extern leverantör, food truck eller enkla tilltugg – men det är ett affärsbeslut, inte ett tillståndsvillkor.",
        },
        {
          h2: "Vem påverkas mest?",
          body: "Cocktailbarer, vinbarer, ölbarer, nattklubbar, caféer och småskaliga koncept gynnas mest eftersom de tidigare ofta fick avslag på köksparagrafen. Klassiska restauranger påverkas mindre i praktiken – kraven sänks men deras verksamhet ser likadan ut.",
        },
        {
          h2: "Vad behöver jag plugga inför kunskapsprovet?",
          body: "Det är fortfarande viktigt att kunna alkohollagens grunder: ordning och nykterhet, ålderskontroll, marknadsföring, serveringsansvar, tillsyn och återkallelse. Frågeantalet och provets exakta upplägg varierar beroende på tillståndstyp och kommun – Folkhälsomyndigheten anger områdena i FoHMFS 2014:7. Vi har uppdaterat övningsfrågorna inför 2026 och flaggat de frågor som rör mat- och köksreglerna så att du övar på rätt version.",
        },
        {
          h2: "Vad händer vid pågående ansökningar?",
          body: "Kommunen tillämpar de regler som gäller vid beslutstillfället. Om din ansökan handläggs när de nya reglerna träder i kraft kan tidigare avslagsgrundande mat- eller köksbrist inte längre användas mot dig. Tveka inte att kontakta kommunen om du har en ansökan inne.",
        },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand", label: "Serveringstillstånd" },
          { to: "/alkohollagen-kapitel-8", label: "Alkohollagen kapitel 8" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/ovningsprov-serveringstillstand", label: "Övningsprov" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/serveringstillstand-bar", label: "För bar & nattklubb" },
          { to: "/serveringstillstand-cafe", label: "För café" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),

  head: () => ({
    meta: [
      { title: "Nya regler serveringstillstånd 2026 – matkravet slopas" },
      { name: "description", content: "1 juni 2026 slopas kravet på eget kök och varierat matutbud för serveringstillstånd. Läs vad som ändras, vad som gäller och hur det påverkar dig." },
      { property: "og:title", content: "Nya regler serveringstillstånd 2026 – köks- och matkravet slopas" },
      { property: "og:description", content: "Riksdagen tar bort köks- och matkraven från 1 juni 2026. Sammanfattning, konsekvenser och vad som gäller framåt." },
      { property: "og:url", content: "https://alkoholprovet.se/nya-regler-serveringstillstand-2026" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/nya-regler-serveringstillstand-2026" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Nya regler 2026", url: "https://alkoholprovet.se/nya-regler-serveringstillstand-2026" },
      ]),
      articleJsonLd({
        url: "https://alkoholprovet.se/nya-regler-serveringstillstand-2026",
        headline: "Nya regler serveringstillstånd 2026 – köks- och matkravet slopas",
        description: "1 juni 2026 slopas kravet på eget kök och varierat matutbud för serveringstillstånd. Läs vad som ändras, vad som gäller och hur det påverkar dig.",
        datePublished: "2026-01-15",
        dateModified: "2026-06-10",
      }),
    ],
  }),
});
